import { InfoOutlined } from "@mui/icons-material";
import { Box, Stack, TextField, Typography, useTheme } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { useLocalization } from "../hooks/useLocalization";
import { Tooltip } from "./Tooltip";

interface RenewalPickerProps {
  value: number | null;
  onChange: (days: number | null) => void;
  label?: string;
  showMobileLayout?: boolean;
  showTooltip?: boolean;
  disabled?: boolean;
}

const MAX_RENEWAL_DAYS = 365;
const DEFAULT_RENEWAL_DAYS = 365;

export const RenewalPicker: FC<RenewalPickerProps> = ({
  value,
  onChange,
  label,
  showTooltip = false,
  disabled = false,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;

      if (inputValue === "") {
        onChange(null);

        return;
      }

      if (!/^\d+$/.test(inputValue)) {
        return;
      }

      const numValue = Number.parseInt(inputValue, 10);

      if (Number.isNaN(numValue)) {
        return;
      }

      // Cap at max value
      if (numValue > MAX_RENEWAL_DAYS) {
        onChange(MAX_RENEWAL_DAYS);

        return;
      }

      onChange(numValue);
    },
    [onChange],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent decimal point, minus sign, plus sign, 'e' (exponential)
    if (
      event.key === "." ||
      event.key === "-" ||
      event.key === "+" ||
      event.key === "e" ||
      event.key === "E"
    ) {
      event.preventDefault();
    }
  };

  const hasError = useMemo(() => {
    if (disabled) return false;
    if (value === null) return true;
    if (value <= 0) return true;
    if (value > MAX_RENEWAL_DAYS) return true;

    return false;
  }, [value, disabled]);

  const errorMessage = useMemo(() => {
    if (!hasError || disabled) return "";
    if (value === null) return t("components.renewalPicker.required");
    if (value <= 0) return t("components.renewalPicker.mustBePositive");
    if (value > MAX_RENEWAL_DAYS)
      return t("components.renewalPicker.maxValue", {
        value: MAX_RENEWAL_DAYS,
      });

    return "";
  }, [hasError, value, t, disabled]);

  return (
    <Stack spacing={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: disabled ? theme.colors.text[0] : theme.colors.text[6],
          }}
        >
          {label ?? t("components.renewalPicker.label")}
        </Typography>
        {showTooltip && (
          <Tooltip
            message={t("components.renewalPicker.tooltip")}
            placement="right"
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: theme.colors.secondary[6],
                cursor: "pointer",
              }}
            />
          </Tooltip>
        )}
      </Box>
      <TextField
        disabled={disabled}
        error={hasError && !disabled}
        helperText={!disabled && errorMessage}
        inputProps={{
          min: 1,
          max: MAX_RENEWAL_DAYS,
          type: "text",
          inputMode: "numeric",
          pattern: "[0-9]*",
          maxLength: 3,
        }}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={String(DEFAULT_RENEWAL_DAYS)}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            bgcolor: theme.colors.grey,
            borderRadius: "16px",
            height: 48,
            border: "none",
            "& fieldset": {
              border: "none",
            },
            "&:hover fieldset": {
              border: "none",
            },
            "&.Mui-focused fieldset": {
              border: "none",
            },
            "&.Mui-disabled": {
              border: "none",
              bgcolor: theme.colors.grey,
            },
            "&.Mui-error": {
              border: `1px solid ${theme.colors.red}`,
            },
          },
          "& .MuiInputBase-input": {
            color: disabled ? theme.colors.text[0] : theme.colors.text[6],
            fontWeight: 500,
            px: "18px",
            "&::placeholder": {
              color: theme.colors.text[0],
              fontSize: 14,
              opacity: 1,
            },
          },
          "& .MuiFormHelperText-root": {
            position: "absolute",
            bottom: "-22px",
            left: "12px",
            right: "12px",
            margin: 0,
            fontSize: "11px",
            padding: "2px 6px",
            borderRadius: "4px",
            color: theme.colors.red,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        value={value ?? ""}
        variant="outlined"
      />
    </Stack>
  );
};

export { DEFAULT_RENEWAL_DAYS, MAX_RENEWAL_DAYS };
